import { Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useUploadProgress, type Adding } from '@/lib/addPhotos'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'
import { Loader } from '@/components/Loader'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// The photos on their way in, each a small square that fills in as it lands.
export function UploadTray({ adding }: { adding: Adding }) {
  const { uploads } = adding
  const busy = uploads.filter((x) => x.state === 'waiting' || x.state === 'adding').length
  const done = uploads.filter((x) => x.state === 'added').length
  const failed = uploads.filter((x) => x.state === 'failed')
  const progress = useUploadProgress(uploads)
  if (busy === 0 && failed.length === 0) return null
  return (
    <div className="flex flex-col gap-4 rounded-[2rem] bg-secondary p-5 sm:p-6">
      <div className="flex flex-col gap-2" role="status">
        <p className="font-medium">
          {busy > 0 ? `Adding photos: ${done} of ${uploads.length} added.` : `${plural(done, 'photo')} added.`}
        </p>
        {busy > 0 && (
          <p className="text-sub text-muted-foreground">
            You can keep looking around while they go in. Keep gatheredfrom open until they’re done.
          </p>
        )}
        <span aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-background">
          <span
            className="block h-full rounded-full bg-primary transition-[width] duration-500"
            style={{ width: `${progress * 100}%` }}
          />
        </span>
      </div>
      <ul aria-hidden="true" className="scroll-row gap-1.5">
        {uploads.map((x, i) => (
          <li key={x.key} className={cn('relative size-14 shrink-0 overflow-hidden rounded-lg', tint(i))}>
            {x.preview && (
              <img
                src={x.preview}
                alt=""
                className={cn('size-full object-cover transition-opacity', x.state !== 'added' && 'opacity-45')}
                onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
              />
            )}
            {x.state === 'added' && (
              <span className="absolute right-1 bottom-1 grid size-4 place-items-center rounded-full bg-primary text-primary-foreground">
                <Check className="size-3" strokeWidth={3} />
              </span>
            )}
            {x.state === 'adding' && <Loader label="Adding" size="sm" now className="absolute inset-0 m-auto" />}
            {x.state === 'failed' && <X className="absolute inset-0 m-auto size-5 text-destructive" strokeWidth={3} />}
          </li>
        ))}
      </ul>
      {failed.length > 0 && (
        <>
          <ul className="flex flex-col gap-1 text-sm text-destructive">
            {failed.map((x) => (
              <li key={x.key}>
                {x.file.name}: {x.error}
              </li>
            ))}
          </ul>
          {busy === 0 && (
            <Button variant="outline" size="sm" className="self-start" onClick={adding.retry}>
              Try those again
            </Button>
          )}
        </>
      )}
    </div>
  )
}
